import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  ClickAwayListener,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ACCOUNT_CONFIGS, INIT_TAX_INFO } from "../../../assets/data/common";
import CountriesData from "../../../assets/data/CountriesData";
import vietnam from "../../../assets/images/flag/VN.png";
import ChevronDown10pxSvg from "../../../assets/images/icons/ChevronDown10pxSvg";
import EditIcon16pxSvgNew from "../../../assets/images/icons/EditIcon16pxSvgNew";
import my_company_logo from "../../../assets/images/logo/my-company-logo.png";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
  PrimaryButton,
  SelectCountryButton,
} from "../../../components/StyledButton";
import {
  CountryMenuList,
  CustomDialog,
} from "../../../components/StyledComponents";
import { StyledLinkBlue } from "../../../components/StyledLink";
import { getCompany, updateCompany } from "../../../axios/Company/CompanyApi";
import { getInfoByTaxcode } from "../../../axios/Taxcode/TaxcodeApi";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";
import { useGetGeneralPolicyCountry } from "../../../hooks/useGeneralService";
import { PhoneRegex } from "../../../utils/regex/Regex";

const organizationSizeData = [
  { value: "1", label: "1-10" },
  { value: "2", label: "11-20" },
  { value: "3", label: "21-50" },
  { value: "4", label: "51-100" },
  { value: "5", label: "100+" },
];

const mapOrganizationSizeToValue = (size) => {
  if (!size) return "";
  const byLabel = organizationSizeData.find((item) => item.label === size);
  if (byLabel) return byLabel.value;
  const byValue = organizationSizeData.find(
    (item) => String(item.value) === String(size)
  );
  return byValue?.value || "";
};

const getEmptyFormData = () => ({
  companyId: "",
  ownerName: "",
  ownerEmail: "",
  company: "",
  address: "",
  phone: "",
  taxCode: "",
  country: "",
  organizationSize: "",
  companyLogo: "",
  langId: localStorage.getItem("i18nextLngId"),
});

const CompanyDetailsDialogForm = () => {
  const { t } = useTranslation();
  const countriesData = CountriesData();
  const [show, hide] = useLoading();
  const { data: countries } = useGetGeneralPolicyCountry();
  const { refreshProfile, updateProfileOptimistic, profile } = useAuth();

  const [taxInfo, setTaxInfo] = useState(INIT_TAX_INFO);
  const [openDetailTaxID, setOpenDetailTaxID] = useState(false);
  const [formData, setFormData] = useState(getEmptyFormData);
  const [prevImageUrl, setPrevImageUrl] = useState(my_company_logo);
  const [isPhoneError, setIsPhoneError] = useState(false);
  const [isPhoneSuccess, setIsPhoneSuccess] = useState(false);
  const [openPhoneCountry, setOpenPhoneCountry] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const anchorRefPhoneCountry = useRef(null);
  const imageInputRef = useRef(null);

  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState({
    flag: vietnam,
    placeHolder: "093 777 4567",
    regex: PhoneRegex.vietnam,
  });

  const [validationMessages, setValidationMessages] = useState({
    phone: "",
    taxCode: "",
  });

  const applyCompanyData = (company) => {
    if (!company) return;

    // Ưu tiên dữ liệu company từ API (sau update); profile chỉ là fallback
    const logo = company.companyLogo || profile?.companyLogo || "";
    setFormData({
      companyId: company.companyId ?? "",
      ownerName: company.ownerName ?? "",
      ownerEmail: company.ownerEmail ?? "",
      company: company.companyName || profile?.company || "",
      address: company.address1 ?? "",
      phone: company.phone ?? "",
      taxCode: company.taxCode ?? "",
      country: company.country ?? "",
      organizationSize: mapOrganizationSizeToValue(company.organizationSize),
      companyLogo: logo,
      langId: localStorage.getItem("i18nextLngId"),
    });
    setPrevImageUrl(
      logo ? `data:image/png;base64,${logo}` : my_company_logo
    );
  };

  const fetchCompany = async ({ withLoading = true } = {}) => {
    if (!profile?.companyId) {
      setLoadingCompany(false);
      return;
    }

    try {
      setLoadingCompany(true);
      if (withLoading) show();
      const response = await getCompany(
        profile.companyId,
        localStorage.getItem("i18nextLngId")
      );
      const list = response?.data?.data;
      const company = Array.isArray(list) ? list[0] : list;
      applyCompanyData(company);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || t("common.network-timeout"));
    } finally {
      setLoadingCompany(false);
      if (withLoading) hide();
    }
  };

  useEffect(() => {
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.companyId]);

  useEffect(() => {
    if (taxInfo.officialName) {
      setFormData((prev) => ({
        ...prev,
        company: taxInfo.officialName,
      }));
    }
  }, [taxInfo.officialName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const digits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 13);

      setFormData((prev) => ({ ...prev, phone: digits }));

      if (digits.length >= 9 && digits.length <= 13) {
        setIsPhoneSuccess(true);
        setIsPhoneError(false);
        setValidationMessages((prev) => ({ ...prev, phone: "" }));
      } else {
        setIsPhoneSuccess(false);
        setIsPhoneError(!!digits);
        setValidationMessages((prev) => ({
          ...prev,
          phone: t("common.content-024"),
        }));
      }
      return;
    }

    if (name === "taxCode") {
      setTaxInfo(INIT_TAX_INFO);
      setValidationMessages((prev) => ({ ...prev, taxCode: "" }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClosePhoneMenu = (event) => {
    if (
      anchorRefPhoneCountry.current &&
      anchorRefPhoneCountry.current.contains(event.target)
    ) {
      return;
    }
    setOpenPhoneCountry(false);
  };

  const toBase64Data = (dataUrlOrBase64) => {
    if (typeof dataUrlOrBase64 !== "string") return "";
    const commaIdx = dataUrlOrBase64.indexOf(",");
    return commaIdx >= 0
      ? dataUrlOrBase64.slice(commaIdx + 1)
      : dataUrlOrBase64;
  };

  const resetDefaultPicture = () => {
    setPrevImageUrl(my_company_logo);
    setFormData((prev) => ({ ...prev, companyLogo: "" }));
  };

  const handleIconClick = () => {
    imageInputRef.current?.click();
  };

  const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  const MIN_RATIO = 0.7;
  const MAX_RATIO = 1.3;

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.warn(t("common.img-accept-file"));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => toast.error(t("common.img-noread"));
    reader.onloadend = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
          toast.warn(t("common.error-img-size"));
          return;
        }
        setPrevImageUrl(dataUrl);
        setFormData((prev) => ({
          ...prev,
          companyLogo: toBase64Data(String(dataUrl)),
        }));
      };
      img.onerror = () => toast.error(t("common.img-noread"));
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleGetInfoByTaxcode = async (taxCode) => {
    try {
      show();
      const contentTaxCode = String(taxCode || "").trim();
      const { data } = await getInfoByTaxcode(contentTaxCode);
      if (data.responseData?.tax_informations[0]) {
        const info = data.responseData.tax_informations[0];
        setTaxInfo({
          officialName: info.company_information.official_name,
          issuanceDate: info.company_information.operation_start_date,
          address: info.company_information.address,
          fullName: info.owner_information.full_name,
          taxId: info.company_information.tax_code,
        });
      } else {
        setValidationMessages((prev) => ({
          ...prev,
          taxCode: t("ERRORMSG_4044"),
        }));
      }
    } catch (error) {
      console.log(error);
    } finally {
      hide();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      show();
      const payload = {
        ...formData,
        address1: formData.address,
        organizationSize: organizationSizeData.find(
          (c) => c.value === formData.organizationSize
        )?.label,
        companyName: formData.company,
        langId: localStorage.getItem("i18nextLngId"),
      };
      const response = await updateCompany(payload);
      if (response.data.responseCode === 0) {
        // Giữ data vừa submit trên form — getCompany ngay sau update có thể trả bản cũ
        updateProfileOptimistic({
          company: formData.company,
          companyLogo: formData.companyLogo,
        });
        setPrevImageUrl(
          formData.companyLogo
            ? `data:image/png;base64,${formData.companyLogo}`
            : my_company_logo
        );
        toast.success(t("common.update-company-successfully"));
        refreshProfile();

        // Sync lại từ BE sau khi kịp cập nhật
        setTimeout(() => {
          fetchCompany({ withLoading: false });
        }, 800);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || t("common.network-timeout"));
    } finally {
      hide();
    }
  };

  return (
    <div className="company-profile">
      <form
        onSubmit={handleSubmit}
        className="card bg-white rounded-2xl border border-[#E5E7EB] p-4 sm:p-6"
      >
        <div className="flex items-center justify-between gap-3 mb-6">
          <p className="text-lg font-semibold text-[#1F2937]">
            {t("sign-up-organization.company-details")}
          </p>
          <ControlpanelPrimaryButton
            type="submit"
            disabled={
              loadingCompany ||
              !formData.taxCode ||
              !formData.company ||
              isPhoneError
            }
          >
            <p>{t("button.update")}</p>
          </ControlpanelPrimaryButton>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Logo */}
          <div className="xl:col-span-3">
            <p className="text-sm text-[#171717] mb-2">
              {t("common.company-logo")}
            </p>
            <div className="flex flex-col gap-2 items-center"><div className="relative inline-block">
              <div className="flex flex items-center justify-center overflow-hidden border border-gray-200 p-[5px] rounded-[4px] bg-white">
                <img
                  src={prevImageUrl}
                  alt="company_logo"
                  className="w-[168px] h-[170px] object-cover rounded-[4px]"
                />
              </div>

              <button
                type="button"
                onClick={handleIconClick}
                className="
                  absolute
                  bottom-0
                  right-0
                  w-10
                  h-10
                  rounded-xl
                  bg-[#0057FF]
                  hover:bg-[#00308C]
                  flex
                  items-center
                  justify-center
                  shadow-lg
                  transition
                "
              >
                <EditIcon16pxSvgNew />
              </button>

              <input
                type="file"
                ref={imageInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>
              <div>
                <StyledLinkBlue
                  onClick={resetDefaultPicture}
                  className="cursor-pointer mt-3 inline-block"
                >
                  <p className="text-sm">{t("common.reset-default")}</p>
                </StyledLinkBlue></div></div>

          </div>

          {/* Form fields */}
          <div className="xl:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
            {/* Tax ID */}
            <div className="md:col-span-2">
              <label className="block text-sm text-[#171717]">
                {t("common.content-139")}
              </label>
              <div className="mt-2 relative flex items-center">
                <input
                  type="text"
                  name="taxCode"
                  className="form-control no-spin-buttons relative z-10 pr-[130px] h-10"
                  value={formData.taxCode}
                  onChange={handleInputChange}
                  disabled
                />
                <PrimaryButton
                  type="button"
                  disabled
                  className="!absolute inset-y-0 right-0 !rounded-none !rounded-r-md !text-nowrap z-10 min-w-[120px]"
                  onClick={() => handleGetInfoByTaxcode(formData.taxCode)}
                >
                  {t("common.get-tax-id")}
                </PrimaryButton>
              </div>
              {validationMessages.taxCode && (
                <p className="text-[14px] text-red-500 mt-1">
                  {t(validationMessages.taxCode)}
                </p>
              )}
            </div>

            {taxInfo.officialName && (
              <div className="md:col-span-2">
                <button
                  type="button"
                  className="flex items-center gap-2 text-blue-500 font-semibold"
                  disabled
                  onClick={() => setOpenDetailTaxID(true)}
                >
                  <OpenInNewIcon className="!w-4 !h-4" />
                  {t("common.content-138")}
                </button>
              </div>
            )}

            {/* Company name */}
            <div className="md:col-span-2">
              <CustomInput
                required
                title={t("common.company-name")}
                inputType="text"
                name="company"
                id="company"
                placeholder={t("common.company-name")}
                handleInputChange={handleInputChange}
                value={formData.company}
                disabled
                readOnly
              />
            </div>

            {/* Owner */}
            <CustomInput
              title={t("common.owner-name")}
              inputType="text"
              name="ownerName"
              id="ownerName"
              readOnly
              placeholder={t("common.owner-name")}
              handleInputChange={handleInputChange}
              value={formData.ownerName}
            />
            <CustomInput
              title={t("common.owner-email")}
              inputType="text"
              name="ownerEmail"
              id="ownerEmail"
              readOnly
              placeholder={t("common.owner-email")}
              handleInputChange={handleInputChange}
              value={formData.ownerEmail}
            />

            {/* Phone */}
            <div className="md:col-span-2">
              <label className="block text-sm text-[#171717]">
                {t("common.phone-number")}
              </label>
              <div className="relative mt-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={13}
                  className={`form-control pl-20 h-10 ${isPhoneError ? "invalid" : ""
                    } ${isPhoneSuccess ? "success" : ""} ${(isPhoneError && validationMessages.phone) || isPhoneSuccess
                      ? "pr-12"
                      : ""
                    }`}
                  placeholder={selectedPhoneCountry.placeHolder}
                  name="phone"
                  id="phone"
                  onChange={handleInputChange}
                  value={formData.phone}
                />
                {isPhoneSuccess && (
                  <span className="absolute inset-y-0 right-0 px-3 flex items-center">
                    <DoneIcon fontSize="small" className="text-green-500" />
                  </span>
                )}
                {isPhoneError && validationMessages.phone && (
                  <span className="absolute inset-y-0 right-0 px-3 flex items-center">
                    <ErrorIcon fontSize="small" className="text-red-500" />
                  </span>
                )}
                <span className="absolute inset-y-0 left-0 flex items-center">
                  <SelectCountryButton
                    type="button"
                    disableRipple
                    onClick={() => setOpenPhoneCountry((prev) => !prev)}
                    ref={anchorRefPhoneCountry}
                    id="phone-country-button"
                    aria-controls={
                      openPhoneCountry ? "phone-country-menu" : undefined
                    }
                    aria-expanded={openPhoneCountry ? "true" : undefined}
                    aria-haspopup="true"
                  >
                    <img
                      src={selectedPhoneCountry.flag}
                      alt=""
                      className="w-[25px]"
                    />
                    <ChevronDown10pxSvg className="w-[10px] ml-2" />
                  </SelectCountryButton>
                </span>
                <Popper
                  open={openPhoneCountry}
                  anchorEl={anchorRefPhoneCountry.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{ width: "100%", zIndex: 10 }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom" ? "left top" : "left bottom",
                      }}
                    >
                      <Paper style={{ borderRadius: 8, width: "100%" }}>
                        <ClickAwayListener onClickAway={handleClosePhoneMenu}>
                          <CountryMenuList
                            autoFocusItem={openPhoneCountry}
                            id="phone-country-menu"
                            aria-labelledby="phone-country-button"
                          >
                            {countriesData.map((value, index) => (
                              <MenuItem
                                key={index}
                                onClick={(e) => {
                                  setSelectedPhoneCountry({
                                    flag: value.flag,
                                    placeHolder: value.placeHolder,
                                    regex: value.phoneRegex,
                                  });
                                  handleClosePhoneMenu(e);
                                }}
                              >
                                <Link>
                                  <div className="flex items-center">
                                    <img
                                      src={value.flag}
                                      alt=""
                                      className="w-[25px]"
                                    />
                                    <p className="ml-2">
                                      {value.countries}
                                      <span className="text-[#C9C9C9] ml-2">
                                        {value.phoneCode}
                                      </span>
                                    </p>
                                  </div>
                                </Link>
                              </MenuItem>
                            ))}
                          </CountryMenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
              {isPhoneError && (
                <p className="text-[14px] text-red-500 mt-1">
                  {validationMessages.phone}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <CustomInput
                title={t("common.address")}
                inputType="text"
                name="address"
                id="address"
                placeholder={t("common.enter-address")}
                handleInputChange={handleInputChange}
                value={formData.address}
              />
            </div>

            {/* Organization size */}
            <div>
              <label className="block text-sm text-[#171717] pb-2">
                {t("common.organization-size")}
              </label>
              <CustomSelect
                name="organizationSize"
                value={formData.organizationSize}
                onChange={handleInputChange}
                options={organizationSizeData}
              />
            </div>

            <div>
              <label className="block text-sm text-[#171717] pb-2">
                {t("common.country")}
              </label>
              <CustomSelect
                name="country"
                className="!bg-white"
                value={formData.country}
                options={countries || []}
                dataType="country"
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    country: value,
                  }));
                }}
              />
            </div>
          </div>
        </div>
      </form>

      <CustomDialog
        maxWidth="xl"
        open={openDetailTaxID}
        onClose={() => setOpenDetailTaxID(false)}
      >
        <DialogTitle>
          <div className="flex justify-between">
            <div className="flex items-center gap-4">
              <p className="text-xl font-semibold text-[#1A1D1F]">
                {t("common.content-138")}
              </p>
            </div>
            <GrayIconButton onClick={() => setOpenDetailTaxID(false)}>
              <CloseIcon />
            </GrayIconButton>
          </div>
        </DialogTitle>
        <DialogContent className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 relative">
          {ACCOUNT_CONFIGS.taxInfo.map(({ key, title, className }) => (
            <CustomInput
              key={key}
              readOnly
              containerClassName={className}
              title={t(title)}
              inputType="text"
              value={taxInfo[key]}
              tooltip={key === "address" ? taxInfo[key] : undefined}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 px-4 py-4">
            <ControlpanelSecondaryButton
              onClick={() => setOpenDetailTaxID(false)}
            >
              <CloseIcon />
              <p>{t("button.close")}</p>
            </ControlpanelSecondaryButton>
          </div>
        </DialogActions>
      </CustomDialog>
    </div>
  );
};

export default CompanyDetailsDialogForm;
